import { useState, useEffect, useCallback } from 'react';
import cryptoHelper from '@/utils/crypto';
import history from '@/utils/history';

export const useSearch = (isSearchActive, initiativesData, workstreamActionsData) => {
  const [isAddMilestoneModalOpen, setIsAddMilestoneModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  
  // Workstream Action Modal States
  const [isWorkstreamActionModalOpen, setIsWorkstreamActionModalOpen] = useState(false);
  const [selectedWorkstreamAction, setSelectedWorkstreamAction] = useState(null);

  const performSearch = useCallback(() => {
    if (!isSearchActive || !searchInputValue) {
      setSearchResults([]);
      return;
    }

    const allResults = [];
    const lowerCaseSearchInput = searchInputValue.toLowerCase();
    const numericSearchValue = Number(searchInputValue);
    const isNumericSearch = !isNaN(numericSearchValue) && searchInputValue.trim() !== '';

    const extractPrimaryNumber = (formattedString) => {
      const match = formattedString.match(/^(?:Initiative|Milestone|Impact|KPI|Workstream Action)\s+(\d+)/i);
      return match ? Number(match[1]) : null;
    };

    if (Array.isArray(initiativesData)) {
      initiativesData.forEach((item) => {
        if (item.milestones) {
          item.milestones.forEach((m) => {
            allResults.push({
              type: 'milestone',
              id: m.milestoneNumber,
              name: m.name,
              initiativeId: item._id,
              formatted: `Milestone ${m.milestoneNumber} - ${m.name}`,
            });
          });
        }
        if (item.impact) {
          item.impact.forEach((i) => {
            allResults.push({
              type: 'impact',
              id: i.impactNumber,
              initiativeId: item._id,
              formatted: `Impact ${i.impactNumber} ${i.typeOfImpact}`,
            });
          });
        }
        if (item.KPI) {
          item.KPI.forEach((k) => {
            allResults.push({
              type: 'kpi',
              id: k.KPINumber,
              initiativeId: item._id,
              formatted: `KPI ${k.KPINumber} ${k.UniversalKPI}`,
            });
          });
        }
        allResults.push({
          type: 'initiative',
          id: item.initiativeNumber,
          name: item.initiativeName,
          _id: item._id,
          formatted: `Initiative ${item.initiativeNumber} - ${item.initiativeName}`,
        });
      });
    }

    if (Array.isArray(workstreamActionsData)) {
      workstreamActionsData.forEach((item) => {
        allResults.push({
          type: 'workstreamAction',
          id: item.actionItemNumber || item.actionNumber,
          _id: item._id,
          name: item.workstreamActionName || item.actionName,
          workstreamActionData: item,
          formatted: `Workstream Action ${item.actionItemNumber || item.actionNumber} - ${item.workstreamActionName || item.actionName}`,
        });
      });
    }

    const filteredResults = allResults.filter((result) =>
      result.formatted.toLowerCase().includes(lowerCaseSearchInput)
    );

    filteredResults.sort((a, b) => {
      const aFormattedLower = a.formatted.toLowerCase();
      const bFormattedLower = b.formatted.toLowerCase();
      const aPrimaryNumber = extractPrimaryNumber(a.formatted);
      const bPrimaryNumber = extractPrimaryNumber(b.formatted);
      const aIsPrimaryNumberMatch = isNumericSearch && aPrimaryNumber === numericSearchValue;
      const bIsPrimaryNumberMatch = isNumericSearch && bPrimaryNumber === numericSearchValue;

      if (aIsPrimaryNumberMatch && !bIsPrimaryNumberMatch) return -1;
      if (!aIsPrimaryNumberMatch && bIsPrimaryNumberMatch) return 1;

      const aIsExactFormatted = aFormattedLower === lowerCaseSearchInput;
      const bIsExactFormatted = bFormattedLower === lowerCaseSearchInput;

      if (aIsExactFormatted && !bIsExactFormatted) return -1;
      if (!aIsExactFormatted && bIsExactFormatted) return 1;

      const aStartsWidthFormatted = aFormattedLower.startsWith(lowerCaseSearchInput);
      const bStartsWidthFormatted = bFormattedLower.startsWith(lowerCaseSearchInput);

      if (aStartsWidthFormatted && !bStartsWidthFormatted) return -1;
      if (!aStartsWidthFormatted && bStartsWidthFormatted) return 1;

      if (isNumericSearch) {
        const regex = new RegExp(`\\b${searchInputValue}\\b`);
        const aContainsWholeNumber = regex.test(aFormattedLower);
        const bContainsWholeNumber = regex.test(bFormattedLower);

        if (aContainsWholeNumber && !bContainsWholeNumber) return -1;
        if (!aContainsWholeNumber && bContainsWholeNumber) return 1;
      }

      return aFormattedLower.localeCompare(bFormattedLower);
    });

    setSearchResults(filteredResults);
  }, [searchInputValue, initiativesData, workstreamActionsData, isSearchActive]);

  useEffect(() => {
    const handler = setTimeout(() => {
      performSearch();
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInputValue, performSearch]);

  const handleSearchInputChange = (value) => {
    setSearchInputValue(value);
  };

  const handleSearchSelect = (value) => {
    const selectedResult = searchResults.find((result) => result.formatted === value);

    if (selectedResult) {
      setSearchResults([]);
      setSearchInputValue('');

      switch (selectedResult.type) {
        case 'initiative':
          const encryptedInitiativeId = cryptoHelper.encrypt(selectedResult._id);
          const encodedInitiativeData = encryptedInitiativeId.replace(/\//g, '_');
          history.push(`initiative/update/${encodedInitiativeData}`);
          break;
        case 'milestone':
          const foundInitiative = initiativesData.find(
            (i) => i._id === selectedResult.initiativeId
          );
          if (foundInitiative) {
            const foundMilestone = foundInitiative.milestones.find(
              (m) => m.milestoneNumber === selectedResult.id
            );
            if (foundMilestone) {
              setSelectedMilestone(foundMilestone);
              setSelectedInitiative(foundInitiative);
              setIsAddMilestoneModalOpen(true);
            }
          }
          break;
        case 'impact':
          const encryptedImpactId = cryptoHelper.encrypt(selectedResult.initiativeId);
          const encodedImpactData = encryptedImpactId.replace(/\//g, '_');
          history.push(`/initiative/update/${encodedImpactData}?section=impacts`);
          break;
        case 'kpi':
          const encryptedKPIId = cryptoHelper.encrypt(selectedResult.initiativeId);
          const encodedKPIData = encryptedKPIId.replace(/\//g, '_');
          history.push(`/initiative/update/${encodedKPIData}?section=KPIs`);
          break;
        case 'workstreamAction':
          // Set the selected workstream action and open modal
          setSelectedWorkstreamAction(selectedResult.workstreamActionData);
          setIsWorkstreamActionModalOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const handleWorkstreamActionModalClose = () => {
    setIsWorkstreamActionModalOpen(false);
    setSelectedWorkstreamAction(null);
  };

  return {
    // Search states
    searchResults,
    searchInputValue,
    
    // Search handlers
    handleSearchInputChange,
    handleSearchSelect,
    
    // Milestone modal states and handlers
    isAddMilestoneModalOpen,
    setIsAddMilestoneModalOpen,
    selectedMilestone,
    selectedInitiative,
    
    // Workstream action modal states and handlers
    isWorkstreamActionModalOpen,
    setIsWorkstreamActionModalOpen,
    selectedWorkstreamAction,
    setSelectedWorkstreamAction,
    handleWorkstreamActionModalClose,
  };
};